/**
 * A chart note is ONE note.
 *
 * "I just noticed an issue with the provider notes. They are broken out into
 *  segments and it has to be one note. Even tho the note says Subjective
 *  Objective Assessment it gets broken out in the same note so we can read it
 *  better it's all one. All the provider notes need to be this way"
 *
 * Her SOAP templates were written into four columns, so the screen gave the
 * provider four boxes and showed four blocks back. The headings are there to
 * make one note readable, not to cut it into four records - so they live INSIDE
 * the text now, and everything from here on is written to the single body.
 *
 * The four columns stay on old charts: a signed note is a legal record and is
 * never rewritten. This is what puts those back together to read and to edit.
 */

export interface NoteParts {
  subjective?: string | null;
  objective?: string | null;
  assessment?: string | null;
  plan?: string | null;
  body?: string | null;
}

const HEADINGS: Array<[keyof NoteParts, string]> = [
  ["subjective", "Subjective"],
  ["objective", "Objective"],
  ["assessment", "Assessment"],
  ["plan", "Plan"],
];

/**
 * One piece of text for a note however it was stored.
 *
 * A note already written as one body comes back untouched. One still held in
 * sections is joined under its own headings, in the order a note is read.
 */
export function mergeNote(parts: NoteParts | null | undefined): string {
  if (!parts) return "";
  const sections = HEADINGS
    .map(([key, label]) => [label, (parts[key] ?? "").trim()] as const)
    .filter(([, text]) => text !== "");
  const body = (parts.body ?? "").trim();

  if (sections.length === 0) return body;
  const joined = sections.map(([label, text]) => `${label}\n${text}`).join("\n\n");
  // A note carrying both (nothing writes this today) keeps everything rather
  // than silently dropping half of it.
  return body ? `${joined}\n\n${body}` : joined;
}

/**
 * What to save. Everything goes in the body; the old columns are cleared, so a
 * note that used to be in sections is not kept twice once it is edited.
 */
export function noteToSave(note: string): Required<NoteParts> {
  return { subjective: "", objective: "", assessment: "", plan: "", body: note };
}
